/**
 * Suppress ResizeObserver errors globally
 * 
 * The "ResizeObserver loop completed with undelivered notifications" error is a benign error
 * that occurs when ResizeObserver can't deliver all notifications within a single animation frame.
 * This typically happens when:
 * 1. An observed element's size changes
 * 2. The size change triggers a state update
 * 3. The state update causes another size change
 * 4. This happens faster than ResizeObserver can process
 * 
 * This error doesn't affect functionality and can be safely suppressed.
 * 
 * Import this file early in your app (e.g., in index.js or App.js)
 */

// Only run in browser environment
if (typeof window !== 'undefined') {
  // Method 1: Intercept at the window.onerror level
  const originalOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    const msg = message?.toString() || '';
    if (
      msg.includes('ResizeObserver loop') ||
      msg.includes('ResizeObserver loop completed with undelivered notifications')
    ) {
      return true; // Prevent default error handling
    }
    if (originalOnError) {
      return originalOnError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Method 2: Suppress the error in the console
  const originalError = console.error;
  const originalWarn = console.warn;

  console.error = (...args) => {
    const errorMessage = String(args[0] || '');
    if (
      errorMessage.includes('ResizeObserver loop') ||
      errorMessage.includes('ResizeObserver') ||
      errorMessage.includes('loop completed with undelivered notifications') ||
      errorMessage.includes('loop limit exceeded')
    ) {
      // Suppress this specific error
      return;
    }
    originalError.apply(console, args);
  };

  console.warn = (...args) => {
    const warnMessage = String(args[0] || '');
    if (
      warnMessage.includes('ResizeObserver loop') ||
      warnMessage.includes('ResizeObserver') ||
      warnMessage.includes('loop completed with undelivered notifications')
    ) {
      // Suppress this specific warning
      return;
    }
    originalWarn.apply(console, args);
  };

  // Method 3: Catch the error event at capture phase
  const resizeObserverErrorHandler = (event) => {
    if (event.message && typeof event.message === 'string') {
      if (
        event.message.includes('ResizeObserver loop') ||
        event.message.includes('ResizeObserver loop completed with undelivered notifications') ||
        event.message.includes('ResizeObserver loop limit exceeded')
      ) {
        event.stopImmediatePropagation();
        event.stopPropagation();
        event.preventDefault();
        return true;
      }
    }
  };

  window.addEventListener('error', resizeObserverErrorHandler, { capture: true });

  // Method 4: Also handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const message = event.reason?.message || String(event.reason || '');
    if (
      message.includes('ResizeObserver loop') ||
      message.includes('ResizeObserver loop completed with undelivered notifications') ||
      message.includes('ResizeObserver loop limit exceeded')
    ) {
      event.preventDefault();
      event.stopPropagation();
      return true;
    }
  });

  // Method 5: Patch ResizeObserver constructor to wrap callbacks
  if (typeof ResizeObserver !== 'undefined') {
    const OriginalResizeObserver = ResizeObserver;
    
    window.ResizeObserver = class PatchedResizeObserver extends OriginalResizeObserver {
      constructor(callback) {
        const wrappedCallback = (entries, observer) => {
          window.requestAnimationFrame(() => {
            try {
              callback(entries, observer);
            } catch (error) {
              if (!error.message?.includes('ResizeObserver')) {
                throw error;
              }
            }
          });
        };
        super(wrappedCallback);
      }
    };
  }
}

// Export empty object for SSR compatibility
export default {};
